import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  CheckboxLineField,
  SequenceMenu,
  useNotifications,
  SimpleModal,
  Box,
  Flex
} from '@sequencehq/core-components'
import { GreyGrey60 } from '@sequencehq/core-components'
import { getEnvironment, isSandboxEnv } from 'lib/environment/environment'
import { usePermissions } from 'lib/rbac'
import { useState } from 'react'
import { useInvoiceBillingAddressGate } from 'Invoices/hooks/useInvoiceBillingAddressGate'

const SendInvoiceModal = (props: {
  invoiceId: string
  onSuccess?: () => void
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()
  const [autoIssue, setAutoIssue] = useState(false)

  const invoiceQuery = useQuery(dashboard20240730Client.getInvoice, {
    id: props.invoiceId
  })

  const customerQuery = useQuery(
    dashboard20240730Client.getCustomer,
    {
      id: invoiceQuery.data?.customerId ?? ''
    },
    {
      enabled: Boolean(invoiceQuery.data?.customerId)
    }
  )
  const billingScheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    {
      id: invoiceQuery.data?.billingScheduleId ?? ''
    },
    {
      enabled: Boolean(invoiceQuery.data?.billingScheduleId)
    }
  )

  const updateBillingScheduleMutation = useMutation(
    dashboard20240730Client.putBillingSchedule
  )
  const { blockSendWhenMissingAddress } = useInvoiceBillingAddressGate(
    props.invoiceId
  )
  const sendMutation = useMutation(dashboard20240730Client.postSendInvoice, {
    onSuccess: () => {
      displayNotification('Invoice sent', {
        type: 'success'
      })
    },
    onError: () => {
      displayNotification('Failed to send invoice', {
        type: 'error'
      })
    }
  })

  const isLoading =
    invoiceQuery.isPending ||
    customerQuery.isPending ||
    billingScheduleQuery.isPending

  const showAutoInvoiceCheckbox =
    billingScheduleQuery.data && !billingScheduleQuery.data.autoIssueInvoices

  return (
    <SimpleModal
      data-testid="invoices.sendInvoice.modal"
      variant="dialog"
      title="Send invoice"
      submitButtonText="Send invoice"
      submittingButtonText="Sending..."
      isLoading={isLoading}
      isOpen
      onClose={props.onClose}
      onSubmit={async () => {
        if (blockSendWhenMissingAddress()) {
          return false
        }

        await Promise.all([
          sendMutation.mutateAsync({ id: props.invoiceId }),
          autoIssue && billingScheduleQuery.data
            ? updateBillingScheduleMutation.mutateAsync({
                id: billingScheduleQuery.data.id,
                body: {
                  ...billingScheduleQuery.data,
                  autoIssueInvoices: true
                }
              })
            : Promise.resolve()
        ])

        props.onSuccess?.()
        return true
      }}
    >
      <Flex flexDirection="column" gap="16px">
        {`Email this invoice to ${customerQuery.data?.legalName} (${(invoiceQuery.data?.customerEmails ?? [])?.join(', ')}).`}

        {showAutoInvoiceCheckbox && (
          <CheckboxLineField
            data-testid="invoices.sendInvoice.autoIssueCheckbox"
            value={autoIssue}
            onChange={newValue => setAutoIssue(newValue)}
            text="Auto-send future invoices created by this Billing Schedule"
          />
        )}
        {isSandboxEnv() && (
          <Box color={GreyGrey60}>
            Note: No end-customer emails will be sent in {getEnvironment()}.
          </Box>
        )}
      </Flex>
    </SimpleModal>
  )
}

export const SendInvoice = (props: {
  invoiceId: string
  onSuccess?: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { can } = usePermissions()

  if (!can('org.invoice:send')) {
    return null
  }

  return (
    <>
      <SequenceMenu.Item
        data-testid="invoices.sendInvoice.menuItem"
        uuid="send-invoice"
        label="Send invoice"
        onClick={e => {
          e.stopPropagation()
          setIsOpen(true)
        }}
      />
      {isOpen && (
        <SendInvoiceModal
          invoiceId={props.invoiceId}
          onSuccess={props.onSuccess}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
